import { List } from "immutable";
import * as moment from "moment";
import * as React from "react";
import * as BS from "react-bootstrap";
import * as Comp from "../../../../components";
import { ComponentWithValidatedForm } from "../../../../components/validation";
import * as Forms from "../../../../forms";
import * as M from "../../../../models";
import { EventTypes } from "../../../../models/event";
import { Api } from "../../../../services/Api";
import { DateHelper, Utility } from "../../../../utils";

interface OwnProps {
    api: Api;
    eventId: number;
    onUpdated: (updatedAbstractIds: number[], evt: M.Event.SubEvent) => void;
}

interface OwnState {
    event?: M.Event.Event;
    loadingEvent: boolean;
    loadingSubEvents: boolean;
    newEvent: boolean;
    selectedAbstractIds: number[];
    show: boolean;
    subEvents: List<M.Event.SubEvent>;
}

export class ScheduleAbstractsModal extends ComponentWithValidatedForm<OwnProps, OwnState> {

    private _isMounted = false;

    public constructor(props: any) {
        super(props);

        const formModel = new Forms.AdminAbstract.AbstractsStatusUpdate();
        this.state = {
            ...this.getInitialState(formModel),
            loadingEvent: false,
            loadingSubEvents: false,
            newEvent: true,
            selectedAbstractIds: [],
            show: false,
            subEvents: List(),
        };
    }

    public componentDidMount() {
        this._isMounted = true;
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public close() {
        this.setState({
            ...this.state,
            newEvent: true,
            selectedAbstractIds: [],
            show: false,
        });
    }

    public show(selectedAbstractIds: number[]) {
        const formModel = new Forms.AdminAbstract.ScheduleAbstracts();
        formModel.data.isNewEvent = true;
        this.setState({ ...this.state,
            ...this.getInitialState(formModel),
            selectedAbstractIds,
            show: true,
        });
    }

    public render() {
        const evt = this.state.event;
        const ids = this.state.selectedAbstractIds;
        const selectedText = `${ids.length} abstract${ids.length === 1 ? "" : "s"}`;
        const loading = (!this.state.event && this.state.loadingEvent)
            || (!this.state.subEvents.size && this.state.loadingSubEvents);

        const events = this.state.subEvents
            .sortBy((subEvt: M.Event.SubEvent) => Utility.fromApiDateToValue(subEvt.startDate))
            .map((subEvt: M.Event.SubEvent) => (Utility.toReactSelectOption(subEvt, subEvt.title, subEvt.id)))
            .toArray();

        const types: M.Editors.SelectItem[] = EventTypes.SubEventTypes.map((v) => ({
            id: v.id,
            render: v.name,
        }));

        const dayItems: M.Editors.SelectItem[] = [];

        if (evt) {
            const ends = Utility.fromApiDateAsMoment(evt.endDate);
            const starts = Utility.fromApiDateAsMoment(evt.startDate);
            const dayCount = ends.diff(starts, "days");
            let currentDay = moment(starts);

            for (let d = 0; d <= dayCount; d += 1) {
                dayItems.push({
                    id: currentDay.format("MM/DD/YYYY"),
                    render: currentDay.format("dddd, MMMM D"),
                });

                // increment the current day
                currentDay = currentDay.add(1, "day");
            }
        }

        return <Comp.Modal.ModalForm
            formComponent={this}
            onCancel={() => this.close()}
            processing={this.state.formProcessing}
            show={this.state.show}
            title={`Schedule abstract${this.state.selectedAbstractIds.length === 1 ? "" : "s"}`}
        >
            <div style={{ position: "relative" }}>
                <Comp.Generic.LoadingSpinner show={loading} />

                <Comp.Reusable.AdminEventLoader
                    eventId={this.props.eventId}
                    onUpdated={(loadingEvt, updatedEvt) => this.onEventUpdate(loadingEvt, updatedEvt)}
                />

                <Comp.Reusable.AdminSubEventsLoader
                    eventId={this.props.eventId}
                    onUpdated={(loadingSubEvents, subEvents) => this.onSubEventsUpdate(loadingSubEvents, subEvents)}
                />

                <Comp.Validation.ValidatedForm
                    formComponent={this}
                    model={this.state.formModel.data}
                    onInputChange={(inputName) => this.handleInputChange(inputName)}
                    onValidSubmit={(model) => this.handleSubmit(model)}
                    schema={this.state.formModel.rules}
                    ref={(ref: any) => this.form = ref}
                >
                    <Comp.Editors.FormField name="" label={`Schedule <strong>${selectedText}</strong> to`}>
                        <BS.ButtonGroup>
                            <BS.Button bsStyle={this.state.newEvent ? "default" : "primary"} onClick={() => this.setNewOrExisting(false)}>
                                An existing event
                            </BS.Button>
                            <BS.Button bsStyle={this.state.newEvent ? "primary" : "default"} onClick={() => this.setNewOrExisting(true)}>
                                A new event
                            </BS.Button>
                        </BS.ButtonGroup>
                    </Comp.Editors.FormField>

                    <BS.Collapse in={this.state.newEvent}>
                        <div>
                            <Comp.Editors.DropdownField formComponent={this} name="dayFormatted" items={dayItems} css="txt" placeholder="Select day..." placeholderValue="" />
                            <Comp.Editors.TimePickerField formComponent={this} name="time" />
                            <Comp.Editors.TextField formComponent={this} name="title" css="txt" />
                            <Comp.Editors.DropdownField formComponent={this} name="typeId" items={types} css="txt" placeholder="Select event type" />
                        </div>
                    </BS.Collapse>

                    <BS.Collapse in={!this.state.newEvent}>
                        <div>
                            <Comp.Editors.SelectField
                                formComponent={this}
                                name="existingEvent"
                                select={{
                                    clearable: false,
                                    options: events,
                                    optionRenderer: (x: M.Event.SubEvent) => this.renderEventForSelect(x),
                                    placeholder: "Select event...",
                                    valueRenderer: (x: M.Event.SubEvent) => this.renderEventForSelect(x),
                                }}
                            />
                        </div>
                    </BS.Collapse>

                </Comp.Validation.ValidatedForm>
            </div>
        </Comp.Modal.ModalForm>;
    }

    private handleInputChange(inputName: string) {
        if (inputName === "dayFormatted") {
            const dayFormatted = this.getFieldValue("dayFormatted") as string;
            const day = moment(dayFormatted, "MM/DD/YYYY");
            this.setFieldValue("day", day.isValid() ? day.toDate() : null);
        }
    }

    private async handleSubmit(model: Forms.AdminAbstract.ScheduleAbstractsData) {
        this.toggleProcessing(true);

        let startDate: moment.Moment | null = null;
        if (model.day && model.time) {
            const day = moment(model.day).format("MM/DD/YYYY");
            const time = moment(model.time).format("HH:mm");
            startDate = moment(`${day} ${time}`, "MM/DD/YYYY HH:mm");
        }

        const body = {
            abstractIds: this.state.selectedAbstractIds,
            existingEventId: model.existingEvent ? model.existingEvent.value : null,
            isNewEvent: model.isNewEvent,
            parentId: this.props.eventId,
            startDate: startDate
                ? DateHelper.formatDateWithoutZone(startDate)
                : null,
            title: model.title,
            typeId: model.typeId,
        };

        const result = await this.props.api.post(Api.endpoints.admin.event.scheduleAbstracts.withUrlParams({ eventId: this.props.eventId }), {
            body: JSON.stringify(body),
        });

        if (!this._isMounted)
            return;

        this.toggleProcessing(false);

        if (result.isSuccess && result.data) {
            this.props.onUpdated(this.state.selectedAbstractIds, result.data);
            this.close();
        }
        else {
            this.setFormErrors(M.Result.Result.errorsOrMessage(result), undefined, true);
        }
    }

    private renderEventForSelect(x: M.Event.SubEvent) {
        const startsOn = Utility.fromApiDateAsMoment(x.startDate);
        const endsOn = Utility.fromApiDateAsMoment(x.endDate);
        const timeRange = DateHelper.displayTimeRange(startsOn, endsOn, "-");
        return <p>
            {startsOn.format("ddd, MMM D")}, {timeRange} - <span dangerouslySetInnerHTML={{ __html: x.title }} />
        </p>;
    }

    private onEventUpdate(loadingEvent: boolean, event: M.Event.Event) {
        this.setState({ ...this.state, loadingEvent, event });
    }

    private onSubEventsUpdate(loadingSubEvents: boolean, subEvents: List<M.Event.SubEvent>) {
        this.setState({ ...this.state, loadingSubEvents, subEvents });
    }

    private setNewOrExisting(newEvent: boolean) {
        this.setState({ ...this.state, newEvent }, () => {
            const formModel = this.getFormModelData<Forms.AdminAbstract.ScheduleAbstractsData>();
            formModel.isNewEvent = this.state.newEvent;
            this.updateFormModelData(formModel);
        });
    }
}



// WEBPACK FOOTER //
// ./src/containers/adminAbstracts/list/components/ScheduleAbstractsModal.tsx